import { Dialog, Paper, PaperProps } from "@mui/material";
import { DialogProps } from "@toolpad/core";
import { SpaceScope } from "~/hooks/useScope";
import { AggregateScoresEdges } from "~/components/FirewatchPage";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";
import { CVEFindingsNode } from "~/pages/inventory/components/AssetSoftwareDetails/AssetSoftwareVulnerabilities/hooks/types";
import { AdvisoryFindingsNode } from "~/pages/inventory/components/Findings/FindingsTable/utils/advisoryTypes";
import { motion } from "motion/react";
import { ExposedAsset } from "~/pages/space/vulnerabilities/components/exposed-assets/types";
import { useCreatePullRequest } from "./hooks/useCreatePullRequest";
import { ANIMATION_DELAYS } from "../../animation";
import { CreatePullRequestStepOne } from "./CreatePullRequestStepOne";
import { CreatePullRequestStepTwo } from "./CreatePullRequestStepTwo";
import { Initiative } from "~/operations";

export interface CreateSecurityPipelinePrDialogPayload {
  aggregateScoresEdges?: AggregateScoresEdges;
  assetMrns?: string[];
  findings?: Array<CVEFindingsNode | AdvisoryFindingsNode | ExposedAsset>;
  initiatives?: Array<Initiative>;
  spaceScope: SpaceScope;
}

export interface CreateSecurityPipelinePrDialogProps
  extends DialogProps<CreateSecurityPipelinePrDialogPayload> {}

export const CreateSecurityPipelinePrDialog = (
  props: CreateSecurityPipelinePrDialogProps,
) => {
  const [showParticles, setShowParticles] = useState<boolean>(false);
  const [animationKey, setAnimationKey] = useState<number>(0);
  const { open, onClose, payload } = props;
  const { aggregateScoresEdges, findings, assetMrns, initiatives } = payload;

  const [numberRect, setNumberRect] = useState<DOMRect | null>(null);
  const [modalRect, setModalRect] = useState<DOMRect | null>(null);
  const numberRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  const {
    handleCreatePullRequestButtonClick,
    isAggregateScoresContext,
    isLoading,
    activeStep,
    workflowUrl,
    manualEffortStats,
    mondooEffortStats,
    manualAndMondooDiffString,
  } = useCreatePullRequest(props);

  useEffect(() => {
    if (showParticles && numberRef.current && modalRef.current) {
      const numberBounds = numberRef.current.getBoundingClientRect();
      const modalBounds = modalRef.current.getBoundingClientRect();
      setNumberRect(numberBounds);
      setModalRect(modalBounds);
    }
  }, [showParticles, numberRef]);

  useEffect(() => {
    if (activeStep !== 1) return;
    setShowParticles(false);
    const timer = setTimeout(
      () => setShowParticles(true),
      ANIMATION_DELAYS.particles * 1000,
    );
    return () => clearTimeout(timer);
  }, [animationKey, activeStep]);

  const handleReplay = useCallback(() => {
    setAnimationKey((prev) => prev + 1);
    setShowParticles(false);
    // Start particles after the badge appears
    setTimeout(() => setShowParticles(true), ANIMATION_DELAYS.particles * 1000);
  }, []);

  const hasNoData =
    !findings?.length && !aggregateScoresEdges?.length && !initiatives?.length;

  if (hasNoData) return null;

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          component: MotionPaper,
          ref: modalRef,
          sx: {
            bgcolor: "background.paper",
            position: "relative",
            overflow: "visible",
            p: 5.5,
            maxWidth: 640,
            minWidth: 640,
          },
        },
      }}
      data-testid="set-exception-dialog"
      onClose={() => {
        onClose();
      }}
    >
      {activeStep === 0 && (
        <CreatePullRequestStepOne
          onClose={onClose}
          isAggregateScoresContext={isAggregateScoresContext}
          aggregateScoresEdges={aggregateScoresEdges}
          initiatives={initiatives}
          cveOrAdvisoryFindings={findings}
          isLoading={isLoading}
          onCreatePullRequestButtonClick={handleCreatePullRequestButtonClick}
        />
      )}
      {activeStep === 1 && (
        <CreatePullRequestStepTwo
          onClose={onClose}
          workflowUrl={workflowUrl || "#"}
          manualEffort={manualEffortStats}
          mondooEffort={mondooEffortStats}
          manualAndMondooDiffString={manualAndMondooDiffString}
          onReplay={handleReplay}
          showParticles={showParticles}
          animationKey={animationKey}
          numberRef={numberRef}
          numberRect={numberRect}
          modalRect={modalRect}
        />
      )}
    </Dialog>
  );
};

const MotionPaper = forwardRef<HTMLDivElement, PaperProps>((props, ref) => {
  return (
    <Paper
      ref={ref}
      layout="position"
      component={motion.div}
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      {...props}
    />
  );
});
